import * as React from "react"
import { Link } from "react-router-dom"
import { Activity, Lock } from "lucide-react"

import { Button } from "../../../ui/button"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../../../ui/empty"

/**
 * MonitorsEmptyState — what Monitor conduct shows when there are no monitors to list.
 *
 * CollectionCardGrid renders it in one of two versions:
 * - canSetUpMonitor: the admin's first action, one button into the existing wizard.
 * - otherwise: nothing has been shared with this person yet, and who to ask.
 *
 * canSetUpMonitor is the same check as the New monitor button in CollectionsHomeTab:
 * MONITORING_CONTRIBUTE and (FIRM_MANAGE or PLATFORM_MANAGE).
 */
export interface MonitorsEmptyStateProps {
  canSetUpMonitor: boolean
  /** The organization admin to ask for access. Shown to people who can't set up a monitor. */
  adminName?: string
}

export function MonitorsEmptyState({ canSetUpMonitor, adminName }: MonitorsEmptyStateProps) {
  if (canSetUpMonitor) {
    return (
      <Empty className="border border-dashed">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Activity />
          </EmptyMedia>
          <EmptyTitle>Set up your first monitor</EmptyTitle>
          <EmptyDescription>
            A monitor brings in conversations from a source such as Intercom on a schedule, and
            Good Outcomes reviews each one for conduct. Your team sees the results here.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button asChild>
            <Link to="/v4/monitor/new">Set up a monitor</Link>
          </Button>
        </EmptyContent>
      </Empty>
    )
  }

  return (
    <Empty className="border border-dashed">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Lock />
        </EmptyMedia>
        <EmptyTitle>No monitors shared with you yet</EmptyTitle>
        <EmptyDescription>
          {adminName
            ? `${adminName}, your admin, decides who sees each monitor. Ask them to give you access.`
            : "Your admin decides who sees each monitor. Ask them to give you access."}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}
